import { STORY_PAUSE_MS, stripTags } from '@/content/story';
import type { ScoreResult } from '@/speech/types';
import { CUES, type Cue, type ErrorType, type Hint } from './cues';
import { fill, PHRASES, PhrasePicker, type AgeBand, type PhraseKey } from './phrases';
import { NOISE_LINES, NoiseCheck, type NoiseAction } from './noiseCheck';

export type { ErrorType, Hint } from './cues';

/**
 * The practice coach: decides what to say after every attempt and when to move on, following a
 * motor-learning cueing hierarchy for /ɹ/ (docs/research/r-practice-feedback.md):
 *
 * 1. unaided try; after a miss, a brief "not yet" and an auditory model
 * 2. second miss: playback of the child's attempt contrasted with the target (a minimal pair
 *    such as wed/red for gliding), with pauses around it to listen; a syllable gets a second
 *    playback on its fourth miss, and the easier syllable after simplifying on its second
 * 3. third miss: a placement cue chosen by the error type (glide, lateral, distortion)
 * 4. fourth miss: simplify to a syllable (rah, ray, ree, roh, roo), then back to the word
 * 5. at most `MAX_MISSES` misses per word, then move on and requeue the word once
 *
 * Progress counts, not only perfection: a clearly better attempt gets specific "closer" praise,
 * and the session moves on with credit when the child improved. Feedback fades (brief knowledge
 * of results only) once the child is mostly correct. Pure logic: no audio, timers or React.
 */

export interface CoachWord {
  id: string;
  text: string;
  /** IPA phonemes of the word. */
  phonemes: readonly string[];
  /** Easier practice target with the same /ɹ/: a consonant-vowel syllable. */
  syllable: { text: string; phonemes: readonly string[] };
  /** The target itself is a practice syllable (no easier step below it). */
  kind?: 'word' | 'syllable';
}

/**
 * How the current word is asked for (docs/design/pedagogy-v2.md): from its picture, by
 * completing a sentence (picture shown), or as a riddle (picture hidden until answered).
 */
export type ExerciseKind = 'say' | 'complete' | 'riddle';

export interface Exercise {
  kind: ExerciseKind;
  /** The sentence or riddle (recorded whole); absent for 'say'. */
  clue?: string;
  /** The word is shown (always, except a riddle not yet answered). */
  revealed: boolean;
}

export interface PracticeTarget {
  kind: 'word' | 'syllable';
  text: string;
  phonemes: readonly string[];
}

/** One spoken attempt as the listener and scorer saw it. */
export interface Attempt {
  result: ScoreResult;
  /** Longer than one word (cut by the endpointer). */
  cut: boolean;
  peakDb: number;
  noiseDb: number;
  /** Share of clipped samples (shouting into the microphone). */
  clippedShare?: number;
}

export type CoachPart =
  | { kind: 'say'; text: string }
  | { kind: 'model'; text: string }
  | { kind: 'playback' }
  | { kind: 'pause'; ms: number }
  /**
   * The child makes a sound now, as a step of a cue ("ee", before sliding back into the r): the
   * app waits until they have said something (or `ms` passed), then goes on. Not judged.
   */
  | { kind: 'try'; text: string; ms: number };

export type Outcome =
  | 'prompt'
  | 'correct'
  | 'closer'
  | 'not-yet'
  | 'not-heard'
  | 'too-quiet'
  | 'too-loud'
  | 'too-long'
  | 'off-target'
  | 'silence'
  | 'mic-pause'
  /** About background noise (noiseCheck.ts): a question, or a note before practice goes on. */
  | 'noise'
  | 'move-on'
  | 'session-end';

export interface CoachTurn {
  outcome: Outcome;
  /** What the app says (and plays) now, in order. */
  parts: CoachPart[];
  /** Short line for the screen, matching what is said. */
  caption: string;
  /** What the child should say next (unchanged unless the turn moves on or simplifies). */
  target: PracticeTarget;
  /** The word changed (or the session ended) with this turn. */
  advanced: boolean;
  /** Earned with this turn: a full reward for a correct word, partial credit for progress. */
  reward: 'success' | 'progress' | null;
  /** How long a pause may last inside one attempt (longer for syllables and stretched sounds). */
  hangoverMs: number;
  /** Error type the cue addressed, if any. */
  cue: ErrorType | null;
  /** Diagram to show with the cue, until the next attempt. */
  hint: Hint | null;
  /** How the current word is asked for. */
  exercise: Exercise;
  /** The child chooses the next word from these (by saying it, or tapping its card). */
  choices: readonly CoachWord[] | null;
  /** Self-check: which of these the child's playback sounded like (tap), or null. */
  selfCheck: SelfCheck | null;
  /** The noise question is open: is a quieter spot available? (answerNoise) */
  noiseQuestion: boolean;
}

/**
 * The transmission stage (epic #44, docs/design/transmission.md): after a mission that uncovers
 * the whole codename, the child says it to Scout, the teammate at headquarters. A clear r earns a
 * bonus star; otherwise Scout understood anyway. Nothing heard is never called an r error.
 */
export interface Transmission {
  /** The codename as spoken, e.g. "Agent Rook". */
  text: string;
  phonemes: readonly string[];
  lines: {
    ask: readonly string[];
    received: readonly string[];
    unclear: readonly string[];
    /** A clear r ("A strong r in Agent Rook!"). */
    clear: string;
    /** Not a clear r ("You must mean Agent Rook!"). */
    understood: string;
    /** Before the first ask: how to send the name (Scout). */
    intro: readonly string[];
  };
}

export type TransmitResult = 'clear' | 'understood';

/**
 * Self-monitoring after playback (docs/research/r-exercises.md: the child judges their own
 * production): the target and the minimal pair the scorer heard, in random order.
 */
export interface SelfCheck {
  options: readonly [string, string];
  /** What the scorer heard (the error form). */
  heard: string;
}

/** A placement cue picked for the dominant error. */
interface PickedCue {
  error: ErrorType;
  text: string;
  hint: Hint;
  /** What is said for it: the cue, or its steps with the child's try between them. */
  parts: CoachPart[];
}

/** How long a cue step waits for the child's sound before going on anyway. */
export const TRY_MS = 4000;

export interface CoachOptions {
  words: readonly CoachWord[];
  /** IPA of the practised sound, e.g. "ɹ", and its label, e.g. "r". */
  sound: { phoneme: string; label: string };
  ageBand?: AgeBand;
  random?: () => number;
  /** Total attempts after which the session wraps up (on a success when possible). */
  maxAttempts?: number;
  /**
   * Let the child choose every other next word from two (principle 5, learner choice). The
   * choice is made by saying one of them, so it costs no practice.
   */
  choices?: boolean;
  /** The clock for spacing the noise check's messages (ms); performance.now by default. */
  now?: () => number;
  /** Sentence and riddle clues for a word (content/exercises.ts); without them, picture only. */
  exercises?: (word: CoachWord) => { complete: string; riddle: string } | undefined;
  /**
   * How the mission opens (docs/design/story.md): a short briefing before the first word. The
   * backstory itself is told on the intro screen (features/intro). Without it, straight to the word.
   */
  briefing?: 'mission';
}

/** Correct words after which a story reminder follows (about one and a few minutes in). */
export const FOE_REMINDER_AT = [3, 9] as const;

/** Failed attempts at one word before moving on. */
export const MAX_MISSES = 5;
/** Failed syllable attempts before moving on. */
export const MAX_SYLLABLE_MISSES = 3;
/**
 * "Closer!": a clear step up from the best attempt so far, as shares of the target's acceptance
 * threshold, so progress is noticed on syllables too (threshold 0.3: a fixed 0.3 minimum could
 * never be met below it, QA 2026-10-11) and stays honest on words (0.75: at least 0.38, up 0.19).
 */
export const CLOSER_GAIN = 0.25;
export const CLOSER_MIN = 0.5;
/** Default threshold when a result does not carry its own (the words' acceptance threshold). */
const DEFAULT_THRESHOLD = 0.75;
const isCloser = (result: ScoreResult, best: number) => {
  const threshold = result.threshold ?? DEFAULT_THRESHOLD;
  const score = result.targetScore;
  return score >= CLOSER_MIN * threshold && score >= best + CLOSER_GAIN * threshold;
};
/** Feedback fades to brief results once this share of recent attempts is correct. */
export const FADE_ACCURACY = 0.8;
const RECENT = 10;
/** Share of clipped samples that means shouting (or a microphone far too close). */
const TOO_LOUD_CLIPPED = 0.01;
/**
 * Quiet around the playback of the child's attempt, so it stands apart from the coach's voice and
 * the child has a moment to listen and compare (was 0 before and 250 ms after; QA 2026-10-11:
 * testers sure their /r/ was right needed to hear themselves more clearly).
 */
export const PAUSE_BEFORE_PLAYBACK_MS = 400;
/**
 * Criterion shaping (challenge point; docs/research/r-acceptance-playback.md): while the child
 * is struggling (recent accuracy at or below SHAPING_ON over at least SHAPING_MIN attempts), an
 * attempt this close below the threshold counts, with honest praise for getting closer, until
 * accuracy is back at SHAPING_OFF. Hitchcock & McAllister 2015 eased the task at 50% and made it
 * harder at 80%; expert listeners themselves agree only moderately on /r/ (Benway 2023).
 */
export const SHAPING_MARGIN = 0.1;
/**
 * Riddles add word recall to the motor task, so they come only while the child is doing well
 * (recent accuracy at least this, over at least RIDDLE_MIN attempts); otherwise the picture or a
 * sentence with the picture. Weights per age band, and no kind three times in a row.
 */
export const RIDDLE_ACCURACY = 0.6;
const RIDDLE_MIN = 3;
const EXERCISE_WEIGHTS: Record<AgeBand, Record<ExerciseKind, number>> = {
  younger: { say: 2, complete: 2, riddle: 1 },
  older: { say: 1, complete: 1, riddle: 1 },
};
const SHAPING_MIN = 6;
const SHAPING_ON = 0.5;
const SHAPING_OFF = 0.8;
export const PAUSE_AFTER_PLAYBACK_MS = 700;
/** Minimum peak above the noise floor for an attempt to be judged. */
/** Sounds children typically produce instead of /ɹ/ (gliding, lateralised, vowel-like). */
const R_SUBSTITUTES = new Set(['w', 'ʋ', 'l', 'ɫ', 'j', 'ʊ', 'ɰ']);
const MIN_SNR_DB = 15;
/**
 * Silence that ends an attempt. Children pause longer than adults, also inside a word (stop
 * closures, a stretched "rrr...ed"), so these are generous; still short enough to answer promptly.
 */
const WORD_HANGOVER_MS = 600;
const SYLLABLE_HANGOVER_MS = 900;
/** A beat between feedback and the next word's prompt. */
const NEXT_WORD_PAUSE_MS = 400;
/** Attempts per session by age band (30-60 for ages 5-7, 70-100 for 8-12). */
const SESSION_ATTEMPTS: Record<AgeBand, number> = { younger: 50, older: 90 };
/** Words in a row without success before an easy win (a word the child already said right). */
const EASY_WIN_AFTER = 2;

interface WordState {
  word: CoachWord;
  misses: number;
  syllableMisses: number;
  onSyllable: boolean;
  syllableSucceeded: boolean;
  best: number;
  improved: boolean;
  errors: Record<ErrorType, number>;
  cueGiven: boolean;
  requeued: boolean;
  /** A model was heard (asked for, or after silence) before any attempt. */
  modeled: boolean;
  exercise: Exercise;
  /** The self-check was asked for this word (at most once). */
  selfChecked: boolean;
}

export class Coach {
  private readonly options: Required<Omit<CoachOptions, 'random' | 'briefing' | 'now'>> &
    Pick<CoachOptions, 'briefing' | 'now'>;
  private readonly picker: PhrasePicker;
  private readonly random: () => number;
  /** Exercise kinds of the latest words, to avoid three of a kind in a row. */
  private recentKinds: ExerciseKind[] = [];
  /** The two words the child is choosing between, if any. */
  private choice: [CoachWord, CoachWord] | null = null;
  /** Words since the last choice (choices alternate with words picked by the coach). */
  private sinceChoice = 0;
  /** The open self-check question, if any. */
  private selfCheck: SelfCheck | null = null;
  /** Eligible second misses so far: the self-check is asked on every other one (dose). */
  private selfCheckChances = 0;
  /** The transmission stage in progress (after the mission), and how it ended. */
  private transmission: {
    target: Transmission;
    unclear: number;
    result: TransmitResult | null;
    /** The coach has said what the codename stage is for. */
    introduced: boolean;
  } | null = null;
  private readonly queue: CoachWord[];
  private readonly requeued = new Set<string>();
  private current: WordState;
  private recent: boolean[] = [];
  private shaping = false;
  /** The attempt being judged counts as a closer approximation (criterion shaping). */
  private approximation = false;
  private attempts = 0;
  /** The mission's briefing has been given. */
  private briefed = false;
  /** Correct words so far (for the story reminders). */
  private correctWords = 0;
  private silences = 0;
  private notHeard = 0;
  private offTarget = 0;
  private mastered: CoachWord[] = [];
  private ended = false;
  /** The mouth picture has been explained once this session (what it shows, tick and cross). */
  private diagramExplained = false;
  /** The latest placement cue, for "hear it again" on its card. */
  private lastCue: PickedCue | null = null;

  constructor(options: CoachOptions) {
    const [first, ...rest] = options.words;
    if (!first) throw new Error('the coach needs at least one word');
    const ageBand = options.ageBand ?? 'younger';
    this.options = {
      ageBand,
      maxAttempts: SESSION_ATTEMPTS[ageBand],
      exercises: () => undefined,
      choices: false,
      ...options,
    };
    this.random = options.random ?? Math.random;
    this.picker = new PhrasePicker(options.random);
    this.queue = rest;
    this.current = this.newWordState(first);
  }

  get word(): CoachWord {
    return this.current.word;
  }

  /** The words the child is choosing between (see CoachOptions.choices), or null. */
  get choices(): readonly CoachWord[] | null {
    return this.choice;
  }

  /**
   * The child chose the next word (by saying it or tapping it); the other one comes next after
   * it. Returns false when there was no choice open or the word is not one of them.
   */
  choose(id: string): boolean {
    const pair = this.choice;
    if (!pair) return false;
    const chosen = pair.find((w) => w.id === id);
    const other = pair.find((w) => w.id !== id);
    if (!chosen || !other) return false;
    this.choice = null;
    this.queue.unshift(other);
    this.current = this.newWordState(chosen, true);
    return true;
  }

  /** How the current word is asked for. */
  get exercise(): Exercise {
    return { ...this.current.exercise };
  }

  get target(): PracticeTarget {
    const sending = this.transmission?.target;
    if (sending) return { kind: 'word', text: sending.text, phonemes: sending.phonemes };
    const { word, onSyllable } = this.current;
    return onSyllable
      ? { kind: 'syllable', ...word.syllable }
      : { kind: word.kind ?? 'word', text: word.text, phonemes: word.phonemes };
  }

  get finished(): boolean {
    return this.ended;
  }

  /** Share of recent attempts that were correct (for fading and difficulty). */
  /** Criterion shaping is on (see SHAPING_MARGIN). */
  get shapingCriterion(): boolean {
    return this.shaping;
  }

  get recentAccuracy(): number {
    return this.recent.length === 0 ? 0 : this.recent.filter(Boolean).length / this.recent.length;
  }

  /** Starts the transmission stage: the next prompt asks for the codename. */
  transmit(target: Transmission): void {
    this.transmission = { target, unclear: 0, result: null, introduced: false };
    this.silences = 0;
    this.selfCheck = null;
    this.choice = null;
  }

  /** The child sent the codename without saying it (the way through without a voice). */
  sendCodename(): CoachTurn {
    const sending = this.transmission;
    if (!sending) throw new Error('No transmission in progress');
    sending.result = 'understood';
    this.ended = true;
    return this.turn(
      'session-end',
      [{ kind: 'say', text: this.picker.pick(sending.target.lines.received, 'transmit:received') }],
      null,
      null,
      true,
    );
  }

  /** How the transmission ended (null until it has). */
  get transmitted(): TransmitResult | null {
    return this.transmission?.result ?? null;
  }

  /** The opening prompt for the current target; the mission's first one starts with a briefing. */
  prompt(): CoachTurn {
    const sending = this.transmission;
    if (sending) {
      // Scout says what the codename stage is for, once; then he asks for the name.
      const intro: CoachPart[] = sending.introduced
        ? []
        : [{ kind: 'say', text: this.picker.pick(sending.target.lines.intro, 'transmit:intro') }];
      sending.introduced = true;
      return this.turn('prompt', [
        ...intro,
        { kind: 'say', text: this.picker.pick(sending.target.lines.ask, 'transmit:ask') },
      ]);
    }
    const briefing = this.briefed ? undefined : this.options.briefing;
    this.briefed = true;
    if (!briefing) return this.turn('prompt', this.ask('prompt', { word: this.target.text }));
    // A short briefing, a breath, then the first word: listen to it, then say it back (a model
    // first, as the practice starts).
    return this.turn('prompt', [
      this.say('missionStart'),
      { kind: 'pause', ms: STORY_PAUSE_MS },
      this.say('firstPrompt', { word: this.target.text }),
    ]);
  }

  /**
   * The child asked for help ("show me how"): a placement cue with its diagram, then a model.
   * Asking for help is a scaffold the child chooses (coaching-framework.md, principle 1), never a
   * miss. Without errors heard yet, the general /r/ cues; otherwise those for the error heard most.
   */
  help(): CoachTurn {
    const state = this.current;
    state.cueGiven = true;
    state.modeled = true;
    state.exercise.revealed = true;
    const vars = this.vars();
    const heardErrors = Object.values(state.errors).some((n) => n > 0);
    const cue = this.pickCue(vars, heardErrors ? undefined : 'distortion');
    return this.turn(
      'prompt',
      [
        this.say('helpIntro', vars),
        ...this.cueParts(cue),
        { kind: 'model', text: this.target.text },
        this.say('yourTurn'),
      ],
      null,
      cue,
    );
  }

  /** Debug: ends the mission now, as after its last word. */
  endNow(): CoachTurn {
    this.ended = true;
    this.selfCheck = null;
    return this.turn('session-end', [this.say('sessionEnd', this.vars())], null, null, true);
  }

  /** The cue card's "hear it again": the latest cue with its steps, a model, then the child. */
  replayCue(): CoachTurn | null {
    const cue = this.lastCue;
    if (!cue) return null;
    return this.turn(
      'prompt',
      [...cue.parts, { kind: 'model', text: this.target.text }, this.say('yourTurn')],
      null,
      cue,
    );
  }

  /**
   * The child put away the open self-check without answering: back to practice with a model of
   * the word.
   */
  dismissQuestion(): CoachTurn | null {
    // Putting the noise question away means practising here.
    if (this.noise.asking) return this.answerNoise(false);
    if (!this.selfCheck) return null;
    this.selfCheck = null;
    return this.turn('prompt', [{ kind: 'model', text: this.target.text }, this.say('yourTurn')]);
  }

  /** A question is open: the child answers by tapping (or puts it away) before practice goes on. */
  get asking(): boolean {
    return this.selfCheck !== null || this.noise.asking;
  }

  /** The child asked to hear the word (or the app offers a model). */
  model(): CoachTurn {
    this.current.modeled = true;
    return this.turn('prompt', [{ kind: 'model', text: this.target.text }, this.say('yourTurn')]);
  }

  /** No speech for a while after the prompt. */
  silence(): CoachTurn {
    this.silences++;
    if (this.transmission) {
      // Ask once more, then wait for the mic (the screen offers "Send the codename" meanwhile).
      if (this.silences === 1) return this.prompt();
      this.silences = 0;
      return this.turn('mic-pause', [this.say('micPause')]);
    }
    const word = this.target.text;
    if (this.silences === 1 && !this.current.exercise.revealed) return this.reveal('riddleHelp');
    if (this.silences === 1) return this.turn('silence', [this.say('silence', { word })]);
    if (this.silences === 2) {
      this.current.modeled = true;
      return this.turn('silence', [{ kind: 'model', text: word }, this.say('yourTurn')]);
    }
    this.silences = 0;
    return this.turn('mic-pause', [this.say('micPause')]);
  }

  /** A sound too short to be a word (cough, tap): ignored unless it keeps happening. */
  discarded(): CoachTurn | null {
    return null;
  }

  attempt(attempt: Attempt): CoachTurn {
    this.silences = 0;
    if (this.transmission) return this.receive(attempt);
    // Speaking instead of tapping answers the question by trying again: it is just an attempt.
    this.selfCheck = null;
    const turn = this.judge(attempt);
    const judged = !['not-heard', 'too-quiet', 'too-long', 'off-target'].includes(turn.outcome);
    if (judged) {
      this.noise.judged();
      return turn;
    }
    // Try after try the helper cannot judge: probably a noisy room or chatter nearby.
    const action = this.noise.unjudgedTry(this.now());
    if (!action) return turn;
    this.notHeard = 0;
    return this.noiseTurn(action);
  }

  /**
   * The listener's steady noise floor (dBFS), about every second while the child's turn is open.
   * A turn to say when the background has stayed loud (noiseCheck.ts), else null.
   */
  noiseFloor(db: number): CoachTurn | null {
    if (this.transmission || this.asking) return null;
    const action = this.noise.floor(db, this.now());
    return action ? this.noiseTurn(action) : null;
  }

  /** The child's answer to the noise question: a quieter spot is available (moving) or not. */
  answerNoise(moving: boolean): CoachTurn | null {
    const action = this.noise.answer(moving, this.now());
    return action ? this.noiseTurn(action) : null;
  }

  /** What the helper says about the noise, then (unless it asks or pauses) the word again. */
  private noiseTurn(action: NoiseAction): CoachTurn {
    const line: CoachPart = { kind: 'say', text: NOISE_LINES[action.line][this.options.ageBand] };
    if (action.pause) return this.turn('mic-pause', [line]);
    if (action.ask) return this.turn('noise', [line]);
    return this.turn('noise', [line, { kind: 'pause', ms: 400 }, ...this.prompt().parts]);
  }

  private now(): number {
    return (this.options.now ?? (() => performance.now()))();
  }

  private judge(attempt: Attempt): CoachTurn {
    const { result } = attempt;
    const word = this.target.text;

    // Problems with the recording itself: never counted as a miss.
    if (!result.heard || attempt.peakDb - attempt.noiseDb < MIN_SNR_DB) {
      this.notHeard++;
      if (this.notHeard >= 3) {
        this.notHeard = 0;
        return this.turn('not-heard', [{ kind: 'model', text: word }, this.say('yourTurn')]);
      }
      return this.turn(this.notHeard === 1 ? 'not-heard' : 'too-quiet', [
        this.say(this.notHeard === 1 ? 'notHeard' : 'tooQuiet', { word }),
      ]);
    }
    this.notHeard = 0;
    // A phrase, not the word ("droplets of water" for "rain", QA 2026-10-11): never accepted,
    // whatever its r sounded like. A riddle described instead of answered gets its answer.
    if (isPhrase(result, this.target.phonemes.length)) {
      if (!this.current.exercise.revealed) return this.reveal('riddleReveal', 'off-target');
      return this.turn('too-long', [this.say('tooLong', { word })]);
    }
    if (attempt.cut) return this.turn('too-long', [this.say('tooLong', { word })]);
    // A riddle answered with another word: a wrong guess, not a pronunciation miss.
    if (!this.current.exercise.revealed && !result.accepted && this.isOtherWord(result)) {
      return this.reveal('riddleReveal', 'off-target');
    }
    if (this.isOffTarget(result) && this.offTarget < 1) {
      this.offTarget++;
      return this.turn('off-target', [this.say('offTarget', { word })]);
    }
    this.offTarget = 0;

    const solvedRiddle = !this.current.exercise.revealed;
    this.current.exercise.revealed = true;

    this.attempts++;
    const loud = (attempt.clippedShare ?? 0) > TOO_LOUD_CLIPPED;
    this.approximation = this.isApproximation(result);
    const judged = this.approximation ? { ...result, accepted: true } : result;
    const turn = this.current.onSyllable ? this.syllableAttempt(judged) : this.wordAttempt(judged);
    this.approximation = false;
    if (solvedRiddle && judged.accepted) turn.parts.unshift(this.say('riddleSolved'));
    turn.caption = captionOf(turn.parts);
    if (loud && !result.accepted) turn.parts.push(this.say('tooLoud'));
    return turn;
  }

  private wordAttempt(result: ScoreResult): CoachTurn {
    const state = this.current;
    const vars = this.vars();
    const faded = this.recentAccuracy >= FADE_ACCURACY && this.recent.length >= 5;
    this.remember(result.accepted);

    if (result.accepted) {
      this.mastered.push(state.word);
      this.noteResult(state, true);
      const key: PhraseKey = this.approximation
        ? 'correctApprox'
        : faded
          ? 'correctBrief'
          : state.cueGiven
            ? 'correctCued'
            : 'correctFirst';
      this.correctWords++;
      const reminder = (FOE_REMINDER_AT as readonly number[]).includes(this.correctWords)
        ? [this.say('foeReminder', vars)]
        : [];
      return this.advance('correct', [this.say(key, vars), ...reminder], 'success');
    }

    const error = errorType(result);
    state.errors[error]++;
    state.misses++;
    const score = result.targetScore;
    const closer = state.misses > 1 && isCloser(result, state.best);
    if (closer) state.improved = true;
    state.best = Math.max(state.best, score);

    if (state.misses >= MAX_MISSES) return this.moveOn();

    const opener = this.say(
      closer ? 'closer' : result.nearMiss ? 'nearMiss' : faded ? 'notYetBrief' : 'notYet',
      vars,
    );
    if (closer) {
      // Progress: encourage repeating the same movement rather than adding a new cue.
      return this.turn('closer', [opener, this.sayAfter(opener, 'promptAgain', vars)], 'progress');
    }
    if (faded && state.misses === 1) {
      return this.turn('not-yet', [opener, this.sayAfter(opener, 'promptAgain', vars)]);
    }
    switch (state.misses) {
      case 1:
        return this.turn('not-yet', [opener, this.say('modelInvite', vars), this.say('yourTurn')]);
      case 2: {
        const check = this.offerSelfCheck(result, error);
        if (check) return this.turn('not-yet', [opener, ...check]);
        return this.turn('not-yet', [opener, ...this.contrast(result, error)]);
      }
      case 3: {
        state.cueGiven = true;
        const cue = this.pickCue(vars);
        return this.turn(
          'not-yet',
          [
            opener,
            ...this.cueParts(cue),
            { kind: 'model', text: state.word.text },
            this.say('yourTurn'),
          ],
          null,
          cue,
        );
      }
      default: {
        if (state.word.kind === 'syllable') {
          // Already the simplest target: listen to it once more (the cue was just given).
          return this.turn('not-yet', [opener, ...this.contrast(result, error)]);
        }
        state.onSyllable = true;
        const syllable = state.word.syllable.text;
        return this.turn('not-yet', [
          opener,
          this.say('simplify', { ...vars, word: syllable }),
          { kind: 'model', text: syllable },
          this.say('yourTurn'),
        ]);
      }
    }
  }

  private syllableAttempt(result: ScoreResult): CoachTurn {
    const state = this.current;
    const vars = { ...this.vars(), word: state.word.syllable.text };
    // Syllable trials count towards recent accuracy too (fading, shaping).
    this.remember(result.accepted);
    if (result.accepted) {
      state.syllableSucceeded = true;
      state.improved = true;
      state.onSyllable = false;
      // One more chance at the word, building on the success.
      state.misses = MAX_MISSES - 1;
      return this.turn(
        'closer',
        [
          this.say('syllableCorrect', vars),
          this.say('backToWord', { ...vars, word: state.word.text }),
        ],
        'progress',
      );
    }
    state.syllableMisses++;
    const score = result.targetScore;
    const closer = isCloser(result, state.best);
    state.best = Math.max(state.best, score);
    if (state.syllableMisses >= MAX_SYLLABLE_MISSES) return this.moveOn();
    if (closer) {
      state.improved = true;
      const closerLine = this.say('closer', vars);
      return this.turn(
        'closer',
        [closerLine, this.sayAfter(closerLine, 'promptAgain', vars)],
        'progress',
      );
    }
    if (state.syllableMisses === 2) {
      // Listening to the easier syllable helps hear the difference too.
      const error = errorType(result);
      return this.turn('not-yet', [
        this.say('notYet', vars),
        ...this.contrast(result, error, vars.word),
      ]);
    }
    const cue = this.pickCue(vars);
    return this.turn(
      'not-yet',
      [
        this.say('notYet', vars),
        ...this.cueParts(cue),
        { kind: 'model', text: vars.word },
        this.say('yourTurn'),
      ],
      null,
      cue,
    );
  }

  /** Playback of the attempt, then a contrast with the target (a minimal pair when possible). */
  /**
   * On every other eligible second miss (a w or l error with a minimal pair), the child hears
   * their attempt and says which word it sounded like, instead of being told.
   */
  private offerSelfCheck(result: ScoreResult, error: ErrorType): CoachPart[] | null {
    const word = this.current.word.text;
    const heard = heardWord(word, result);
    if (!heard || error === 'distortion' || this.current.selfChecked) return null;
    if (this.selfCheckChances++ % 2 === 1) return null;
    this.current.selfChecked = true;
    const options: [string, string] = this.random() < 0.5 ? [word, heard] : [heard, word];
    this.selfCheck = { options, heard };
    return [
      this.say('playbackIntro'),
      { kind: 'pause', ms: PAUSE_BEFORE_PLAYBACK_MS },
      { kind: 'playback' },
      { kind: 'pause', ms: PAUSE_AFTER_PLAYBACK_MS },
      this.say('selfCheck', { ...this.vars(), word: options[0], heard: options[1] }),
    ];
  }

  /**
   * The child's answer to the self-check. Agreeing with the scorer confirms their ear; otherwise
   * the helper says what it heard and plays theirs and the model again. Never counted as a try.
   */
  answerSelfCheck(answer: string): CoachTurn | null {
    const check = this.selfCheck;
    if (!check) return null;
    this.selfCheck = null;
    const vars = { ...this.vars(), heard: check.heard };
    const word = this.current.word.text;
    if (answer === check.heard) {
      return this.turn('prompt', [
        this.say('selfCheckAgree', vars),
        { kind: 'model', text: word },
        this.say('yourTurn'),
      ]);
    }
    // The child heard it differently from the scorer: say what it heard, once and gently, and let
    // them hear both (no drill to "correct" their ear: with two words that felt pedantic, QA
    // 2026-10-11).
    return this.turn('prompt', [
      this.say('selfCheckDisagree', vars),
      { kind: 'pause', ms: PAUSE_BEFORE_PLAYBACK_MS },
      { kind: 'playback' },
      { kind: 'pause', ms: PAUSE_AFTER_PLAYBACK_MS },
      { kind: 'model', text: word },
      this.say('yourTurn'),
    ]);
  }

  private contrast(
    result: ScoreResult,
    error: ErrorType,
    target = this.current.word.text,
  ): CoachPart[] {
    const vars = { ...this.vars(), word: target };
    const heard = heardWord(target, result);
    const parts: CoachPart[] = [
      this.say('playbackIntro'),
      { kind: 'pause', ms: PAUSE_BEFORE_PLAYBACK_MS },
      { kind: 'playback' },
      { kind: 'pause', ms: PAUSE_AFTER_PLAYBACK_MS },
    ];
    if (heard && error !== 'distortion') {
      parts.push(
        this.say(error === 'glide' ? 'contrastGlide' : 'contrastLateral', { ...vars, heard }),
      );
      parts.push({ kind: 'model', text: target });
    } else {
      parts.push(this.say('contrastDistortion', vars));
    }
    parts.push(this.say('yourTurn'));
    return parts;
  }

  private moveOn(): CoachTurn {
    const state = this.current;
    this.noteResult(state, false);
    const progressed = state.improved;
    this.unsuccessfulWords++;
    // "We'll come back to it" only when the word really comes back.
    const returns = !this.finishingStrong && !this.requeued.has(state.word.id);
    if (returns) {
      this.requeued.add(state.word.id);
      this.queue.push(state.word);
    }
    const key: PhraseKey = progressed
      ? returns
        ? 'moveOnProgressReturn'
        : 'moveOnProgress'
      : returns
        ? 'moveOnReturn'
        : 'moveOn';
    return this.advance('move-on', [this.say(key, this.vars())], progressed ? 'progress' : null);
  }

  /** Moves to the next word (or ends), appending the new prompt to `parts`. */
  private advance(outcome: Outcome, parts: CoachPart[], reward: CoachTurn['reward']): CoachTurn {
    if (outcome === 'correct') this.unsuccessfulWords = 0;
    const pause: CoachPart = { kind: 'pause', ms: NEXT_WORD_PAUSE_MS };
    // Frustration guard: after several hard words in a row, an easy win before going on.
    const easyWin =
      outcome === 'move-on' && this.unsuccessfulWords >= EASY_WIN_AFTER
        ? this.mastered.find((w) => w.id !== this.current.word.id)
        : undefined;
    if (easyWin && !this.finishingStrong && this.attempts < this.options.maxAttempts) {
      this.unsuccessfulWords = 0;
      this.current = this.newWordState(easyWin, true);
      const vars = { ...this.vars(), word: easyWin.text };
      return this.turn(outcome, [...parts, pause, this.say('easyWin', vars)], reward, null, true);
    }
    const next =
      this.finishingStrong || this.attempts >= this.options.maxAttempts
        ? undefined
        : this.queue.shift();
    if (!next) {
      // End on a success: if the last word was not correct, finish with a word the child knows.
      const easy = this.mastered.find((w) => w.id !== this.current.word.id) ?? this.mastered[0];
      if (outcome !== 'correct' && easy && !this.finishingStrong) {
        this.finishingStrong = true;
        this.queue.length = 0;
        this.current = this.newWordState(easy, true);
        const vars = { ...this.vars(), word: easy.text };
        return this.turn(outcome, [...parts, pause, this.say('easyWin', vars)], reward, null, true);
      }
      this.ended = true;
      return this.turn(
        'session-end',
        [...parts, this.say('sessionEnd', this.vars())],
        reward,
        null,
        true,
      );
    }
    const again =
      this.requeued.has(next.id) && next.id !== this.current.word.id && this.wasSeen(next);
    const alternative = this.queue[0];
    if (this.options.choices && !again && alternative && this.sinceChoice >= 1) {
      // Two pictures; the child says (or taps) the one to practise next.
      this.queue.shift();
      this.choice = [next, alternative];
      this.sinceChoice = 0;
      this.current = this.newWordState(next, true);
      return this.turn(
        outcome,
        [
          ...parts,
          pause,
          this.say('chooseNext'),
          { kind: 'model', text: next.text },
          { kind: 'pause', ms: 300 },
          { kind: 'model', text: alternative.text },
        ],
        reward,
        null,
        true,
      );
    }
    this.sinceChoice++;
    this.current = this.newWordState(next, again);
    this.current.requeued = again;
    const vars = { ...this.vars(), word: next.text };
    return this.turn(
      outcome,
      [...parts, pause, ...(again ? [this.say('requeue', vars)] : this.ask('prompt', vars))],
      reward,
      null,
      true,
    );
  }

  private finishingStrong = false;
  private readonly noise = new NoiseCheck();
  private unsuccessfulWords = 0;
  private readonly seen = new Set<string>();

  private wasSeen(word: CoachWord): boolean {
    return this.seen.has(word.id);
  }

  /**
   * How the mission went (for stars): targets practised, said correctly at some attempt, and
   * correct at the first attempt without a model or cue.
   */
  summary(): {
    targets: number;
    correct: number;
    firstTry: number;
    attempts: number;
    bestStreak: number;
  } {
    const all = [...this.results.values()];
    return {
      targets: all.length,
      correct: all.filter((r) => r.correct).length,
      firstTry: all.filter((r) => r.firstTry).length,
      attempts: this.attempts,
      bestStreak: this.bestStreak,
    };
  }

  /** Accepted attempts in a row now, and the longest run this session (never taken away). */
  private streak = 0;
  private bestStreak = 0;

  private readonly results = new Map<string, { correct: boolean; firstTry: boolean }>();

  private noteResult(state: WordState, correct: boolean) {
    const before = this.results.get(state.word.id);
    const firstVisit = before === undefined;
    const firstTry =
      correct && firstVisit && state.misses === 0 && !state.modeled && !state.syllableSucceeded;
    this.results.set(state.word.id, {
      correct: correct || (before?.correct ?? false),
      firstTry: firstTry || (before?.firstTry ?? false),
    });
  }

  private newWordState(word: CoachWord, plain = false): WordState {
    this.seen.add(word.id);
    return {
      word,
      exercise: plain ? { kind: 'say', revealed: true } : this.pickExercise(word),
      misses: 0,
      syllableMisses: 0,
      onSyllable: false,
      syllableSucceeded: false,
      best: 0,
      improved: false,
      errors: { glide: 0, lateral: 0, distortion: 0 },
      cueGiven: false,
      requeued: false,
      modeled: false,
      selfChecked: false,
    };
  }

  /** The exercise for a word: rotated, weighted by age band, riddles only while doing well. */
  private pickExercise(word: CoachWord): Exercise {
    const clues = word.kind === 'syllable' ? undefined : this.options.exercises(word);
    if (!clues) return { kind: 'say', revealed: true };
    const doingWell = this.recent.length >= RIDDLE_MIN && this.recentAccuracy >= RIDDLE_ACCURACY;
    const weights = EXERCISE_WEIGHTS[this.options.ageBand];
    const [a, b] = this.recentKinds.slice(-2);
    const kinds = (Object.keys(weights) as ExerciseKind[]).filter(
      (k) => (k !== 'riddle' || doingWell) && !(k === a && k === b),
    );
    const total = kinds.reduce((n, k) => n + weights[k], 0);
    let r = this.random() * total;
    const kind = kinds.find((k) => (r -= weights[k]) < 0) ?? 'say';
    this.recentKinds = [...this.recentKinds, kind].slice(-2);
    if (kind === 'say') return { kind, revealed: true };
    return { kind, clue: clues[kind], revealed: kind !== 'riddle' };
  }

  /** How the current word is asked for: its clue, or the prompt phrase. */
  private ask(key: PhraseKey, vars: Record<string, string>): CoachPart[] {
    const { exercise } = this.current;
    if (exercise.clue && !this.current.onSyllable) return [{ kind: 'say', text: exercise.clue }];
    return [this.say(key, vars)];
  }

  private remember(correct: boolean) {
    this.recent = [...this.recent, correct].slice(-RECENT);
    this.streak = correct ? this.streak + 1 : 0;
    this.bestStreak = Math.max(this.bestStreak, this.streak);
    if (this.recent.length >= SHAPING_MIN && this.recentAccuracy <= SHAPING_ON) this.shaping = true;
    else if (this.recentAccuracy >= SHAPING_OFF) this.shaping = false;
  }

  /** The riddle's answer is shown and said: after a wrong guess, or when the child is stuck. */
  private reveal(key: 'riddleReveal' | 'riddleHelp', outcome: Outcome = 'silence'): CoachTurn {
    this.current.exercise.revealed = true;
    this.current.modeled = true;
    const word = this.target.text;
    // The answer line says the word itself (spliced like a model).
    return this.turn(outcome, [this.say(key, { word }), this.say('yourTurn')]);
  }

  /**
   * Another word than the answer: the word's other sounds are not there either. More lenient
   * than off-target detection, since a riddle invites guesses ("bunny" for rabbit).
   */
  private isOtherWord(result: ScoreResult): boolean {
    const others = result.phonemes.filter((_, i) => i !== result.targetIndex);
    if (others.length === 0) return false;
    const mean = others.reduce((s, p) => s + p.score, 0) / others.length;
    return mean < 0.3 && result.targetScore < 0.5;
  }

  /** While shaping: a miss just below the threshold counts as a closer approximation. */
  private isApproximation(result: ScoreResult): boolean {
    if (!this.shaping || result.accepted || result.threshold === undefined) return false;
    const margin = Math.min(SHAPING_MARGIN, result.threshold / 3);
    return result.targetScore >= result.threshold - margin;
  }

  private dominantError(): ErrorType {
    const { errors } = this.current;
    return (Object.keys(errors) as ErrorType[]).reduce((a, b) => (errors[b] > errors[a] ? b : a));
  }

  /**
   * The child said something else: the non-target sounds are very unlike the word and the target
   * sound is not there either.
   */
  private isOffTarget(result: ScoreResult): boolean {
    // "woh" for "roh": the usual /r/ substitutions are an /r/ error, not another word, even when
    // a short syllable's other sound is misheard too.
    const heardAs = result.phonemes[result.targetIndex]?.heardAs;
    if (heardAs && R_SUBSTITUTES.has(heardAs)) return false;
    const others = result.phonemes.filter((_, i) => i !== result.targetIndex);
    // A practice syllable ("ray") has one other sound: one misheard vowel is not another word, and
    // "I was listening for ray" to a child who said a distorted "ray" is confusing.
    if (others.length < 2) return false;
    const mean = others.reduce((s, p) => s + p.score, 0) / others.length;
    return mean < 0.12 && result.targetScore < 0.2;
  }

  private vars(): Record<string, string> {
    return { word: this.target.text, sound: this.options.sound.label };
  }

  /** A placement cue for the error heard most on this word, with its diagram. */
  private pickCue(vars: Record<string, string>, only?: ErrorType): PickedCue {
    const error = only ?? this.dominantError();
    const cues = CUES[this.options.ageBand][error];
    const text = this.picker.pick(
      cues.map((c) => c.text),
      `${this.options.ageBand}:cue:${error}`,
    );
    const cue: Cue = cues.find((c) => c.text === text) ?? { text, hint: { shows: 'r-bunched' } };
    const step = cue.step;
    const parts: CoachPart[] = step
      ? [
          { kind: 'say', text: fill(step.first, vars) },
          { kind: 'try', text: step.sound, ms: TRY_MS },
          { kind: 'say', text: fill(step.then, vars) },
        ]
      : [{ kind: 'say', text: fill(text, vars) }];
    const hint: Hint = {
      ...cue.hint,
      steps: step
        ? [{ text: fill(step.first, vars), try: step.sound }, { text: fill(step.then, vars) }]
        : [{ text: fill(text, vars) }],
    };
    const picked = { error, text: fill(text, vars), hint, parts };
    this.lastCue = picked;
    return picked;
  }

  /** A cue's parts, after a one-time explanation of the mouth picture the first time one shows. */
  private cueParts(cue: PickedCue): CoachPart[] {
    if (this.diagramExplained) return cue.parts;
    this.diagramExplained = true;
    return [this.say(cue.hint.insteadOf ? 'hintIntroPair' : 'hintIntro'), ...cue.parts];
  }

  /**
   * A line after `previous` that does not repeat its "again" ("Nearly! Again, with more r."
   * then "Again: rock.", QA 2026-10-11); any line of the bank when all of them would.
   */
  private sayAfter(previous: CoachPart, key: PhraseKey, vars: Record<string, string>): CoachPart {
    const again = /\bagain\b/i;
    if (previous.kind !== 'say' || !again.test(previous.text)) return this.say(key, vars);
    const bank = PHRASES[this.options.ageBand][key];
    const fresh = bank.filter((line) => !again.test(line));
    if (fresh.length === 0) return this.say(key, vars);
    return {
      kind: 'say',
      text: fill(this.picker.pick(fresh, `${this.options.ageBand}:${key}:fresh`), vars),
    };
  }

  private say(key: PhraseKey, vars: Record<string, string> = this.vars()): CoachPart {
    const bank = PHRASES[this.options.ageBand];
    return {
      kind: 'say',
      text: fill(this.picker.pick(bank[key], `${this.options.ageBand}:${key}`), vars),
    };
  }

  /**
   * Scout receives the codename: a clear r earns the bonus star, anything else heard is
   * understood anyway. A sound that was not heard as speech gets one "say it again".
   */
  private receive({ result }: Attempt): CoachTurn {
    const sending = this.transmission;
    if (!sending) throw new Error('No transmission in progress');
    const { lines } = sending.target;
    if (!result.heard && sending.unclear < 1) {
      sending.unclear++;
      return this.turn('not-heard', [
        { kind: 'say', text: this.picker.pick(lines.unclear, 'transmit:unclear') },
      ]);
    }
    sending.result = result.accepted ? 'clear' : 'understood';
    this.ended = true;
    return this.turn(
      'session-end',
      [
        { kind: 'say', text: result.accepted ? lines.clear : lines.understood },
        { kind: 'pause', ms: 300 },
        { kind: 'say', text: this.picker.pick(lines.received, 'transmit:received') },
      ],
      result.accepted ? 'success' : null,
      null,
      true,
    );
  }

  private turn(
    outcome: Outcome,
    parts: CoachPart[],
    reward: CoachTurn['reward'] = null,
    cue: PickedCue | null = null,
    advanced = false,
  ): CoachTurn {
    const target = this.target;
    return {
      outcome,
      parts,
      caption: captionOf(parts),
      target,
      advanced,
      reward,
      hangoverMs: target.kind === 'syllable' ? SYLLABLE_HANGOVER_MS : WORD_HANGOVER_MS,
      cue: cue?.error ?? null,
      hint: cue?.hint ?? null,
      exercise: { ...this.current.exercise },
      choices: this.choice,
      selfCheck: this.selfCheck,
      noiseQuestion: this.noise.asking,
    };
  }
}

/**
 * Heard many more sounds than the word has: a different phrase or a sentence, not the word said
 * once or twice ("rain rain", a stretched "rrrain" and "it's rain" stay within it).
 */
export function isPhrase(result: ScoreResult, wordSounds: number): boolean {
  const heard = result.heardSounds;
  return heard !== undefined && heard > Math.max(6, 2 * wordSounds + 2);
}

/** The screen line: what is said, in order. */
function captionOf(parts: readonly CoachPart[]): string {
  return parts
    .filter((p): p is { kind: 'say'; text: string } => p.kind === 'say')
    .map((p) => stripTags(p.text))
    .join(' ');
}

/** Error type from what the target sound was heard as. */
export function errorType(result: ScoreResult): ErrorType {
  const heard = result.phonemes[result.targetIndex]?.heardAs;
  if (heard === 'w' || heard === 'ʊ' || heard === 'u') return 'glide';
  if (heard === 'l') return 'lateral';
  return 'distortion';
}

/** The word as heard, for a minimal-pair contrast: "red" heard with /w/ -> "wed". */
export function heardWord(text: string, result: ScoreResult): string | null {
  const error = errorType(result);
  if (error === 'distortion') return null;
  return misheard(text, error === 'glide' ? 'w' : 'l');
}

/** Spellings for words whose /r/ is not a plain leading "r" ("rhino" is not "whino"). */
const MISHEARD: Record<string, { w: string; l: string }> = { rhino: { w: 'wyno', l: 'lyno' } };

/** The word with its leading /r/ said as w (gliding) or l (lateral); null if it has none. */
export function misheard(text: string, as: 'w' | 'l'): string | null {
  const special = MISHEARD[text.toLowerCase()];
  if (special) return special[as];
  return /^r/i.test(text) ? as + text.slice(1) : null;
}
